import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/react';
import { useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { Icons } from '@/components/ui/icons';
import { hasPendingSubstituteApplication } from '@/lib/pending-substitute-application';
import { trackRegistrationConversion } from '@/lib/gtm';
import { consumeStoredAuthRedirect } from '@/lib/auth-redirect';
import { redirectToUrlReplacingHistory } from '@/lib/redirect';

const SSOCallback = () => {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();
  const navigate = useNavigate();
  const setMyNameParts = useMutation(api.app.users.mutations.setMyNameParts as any);
  const upsertMyUserProfile = useMutation(api.app.users.mutations.upsertMyUserProfile as any);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      // Wait for both auth and user to be loaded
      if (!authLoaded || !userLoaded) return;

      // If not signed in, something went wrong
      if (!isSignedIn || !user) {
        setError('Authentication failed. Please try again.');
        setTimeout(() => {
          void navigate({ to: '/auth/sign-up', replace: true });
        }, 2000);
        return;
      }

      // Avoid duplicate processing
      if (processing) return;
      setProcessing(true);

      try {
        // Retrieve role and MVZ status from localStorage
        const pendingRole = localStorage.getItem('pendingUserRole') as 'provider' | 'substitute' | null;
        const pendingMvz = localStorage.getItem('pendingUserMvz') === 'true';

        // If no role found, default to substitute (shouldn't happen with proper flow)
        const role = pendingRole || 'substitute';

        // Extract name from user (OAuth providers give us this)
        const firstName = user.firstName || 'User';
        const lastName = user.lastName || 'Name';

        // Save user data with role to Convex (Convex is source of truth)
        // Retry logic for race condition where Clerk session might not be immediately available
        const maxAttempts = 8;
        let attempt = 0;
        await new Promise((resolve) => setTimeout(resolve, 100));

        while (attempt < maxAttempts) {
          try {
            await setMyNameParts({
              firstName,
              lastName,
              role,
            });
            break;
          } catch (err: unknown) {
            const message = (err as any)?.message || '';
            if (typeof message === 'string' && message.includes('Not authenticated')) {
              attempt += 1;
              await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
              continue;
            }
            throw err;
          }
        }

        // DA-326: Set MVZ status for providers after user creation
        if (role === 'provider' && pendingMvz) {
          try {
            await upsertMyUserProfile({ isMvz: true });
          } catch (err: unknown) {
            console.error('Failed to set MVZ status (non-critical):', err);
            // Don't block the user - they can update this in profile settings
          }
        }

        // Clear the pending role and MVZ from localStorage
        localStorage.removeItem('pendingUserRole');
        localStorage.removeItem('pendingUserMvz');

        // DA-431: Check for pending substitute application from homepage checkout flow
        const hasPendingApplication = role === 'substitute' && hasPendingSubstituteApplication();

        // Track registration conversion for Google Ads (if consent granted)
        trackRegistrationConversion();

        // Redirect to role-specific dashboard; provider draft continuation is handled
        // after auth inside the protected onboarding shell.
        if (role === 'provider') {
          redirectToUrlReplacingHistory(consumeStoredAuthRedirect({ fallbackPath: '/provider/dashboard' }));
        } else {
          // DA-431: If substitute has a pending application, let the OnboardingGate handle
          // the redirect to the service detail page after onboarding completes
          const fallbackPath = hasPendingApplication ? '/substitute/dashboard?openApply=true' : '/substitute/dashboard';
          redirectToUrlReplacingHistory(consumeStoredAuthRedirect({ fallbackPath }));
        }
      } catch (err: unknown) {
        console.error('Error processing SSO callback:', err);
        setError('Failed to complete registration. Please try again.');
        setTimeout(() => {
          void navigate({ to: '/auth/sign-up', replace: true });
        }, 2000);
      }
    };

    void processCallback();
  }, [authLoaded, userLoaded, isSignedIn, user, processing, navigate, setMyNameParts, upsertMyUserProfile]);

  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background'>
        <div className='text-center'>
          <div className='text-destructive mb-4'>
            <Icons.AlertCircle className='w-12 h-12 mx-auto' />
          </div>
          <p className='text-lg text-foreground mb-2'>Authentication Error</p>
          <p className='text-sm text-muted-foreground'>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-background'>
      <div className='text-center'>
        <Icons.Loader className='w-12 h-12 mx-auto mb-4 animate-spin text-primary' />
        <p className='text-lg text-foreground mb-2'>Completing registration...</p>
        <p className='text-sm text-muted-foreground'>Please wait while we set up your account.</p>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/auth/sso-callback')({
  component: SSOCallback,
});
